import React, { useState } from 'react';
import {
	Alert,
	Button,
	Card,
	CardBody,
	CardTitle,
	Col,
	Container,
	Form,
	FormFeedback,
	Input,
	Label,
	Row,
} from 'reactstrap';
import { useForm } from '@mantine/form';
import { Link, useNavigate } from 'react-router-dom'; // useNavigate for potential redirect after success
import { LoadingOverlay } from '@mantine/core';
import { AppPaths } from '@/configs/appPaths'; // Assuming LOGIN path is defined here
import logo from '@/assets/images/logo.svg';
import { SubmitStatus } from './Login'; // Reuse SubmitStatus enum from Login
import UnauthenticatedApi from '@/api/UnauthenticatedApi'; // Import your API handler
import { getErrorCodes } from '@/utils/utilities'; // Import error handler if needed
import { useMutation } from '@tanstack/react-query';

export type RegisterForm = {
	companyName: string;
	firstName: string;
	lastName: string;
	contactPhoneNumber: string;
	contactEmail: string;
	password: string;
	repeatPassword: string;
	terms: boolean;
	recaptchaToken?: string;
};

export interface RegisterValues {
	companyName: string;
	contactPhoneNumber: string;
	contactEmail: string;
	password: string;
	confirmPassword: string;
	acceptTerms: boolean;
	acceptPrivacyPolicy: boolean;
}

const initialValues: RegisterValues = {
	companyName: '',
	contactPhoneNumber: '',
	contactEmail: '',
	password: '',
	confirmPassword: '',
	acceptTerms: false,
	acceptPrivacyPolicy: false,
};

export const Register: React.FC = () => {
	const navigate = useNavigate();
	const [status, setStatus] = useState(SubmitStatus.THROTTLE);
	const [resultMessage, setResultMessage] = useState('');

	const form = useForm({
		initialValues,
		validate: {
			companyName: (value) =>
				value.trim().length > 0 ? null : 'Nazwa firmy jest wymagana',
			contactEmail: (value) => {
				if (!value || value.trim() === '') return 'Adres email jest wymagany';

				const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
				if (!emailRegex.test(value)) {
					return 'Nieprawidłowy format adresu email';
				}

				return null;
			},
			password: (value) =>
				value.length >= 6 ? null : 'Hasło musi mieć co najmniej 6 znaków',
		},
	});

	const registerMutation = useMutation({
		mutationFn: UnauthenticatedApi.register,
		onMutate: () => {
			setStatus(SubmitStatus.LOADING);
			setResultMessage('');
		},
		onSuccess: () => {
			setStatus(SubmitStatus.SUCCESS);
			setResultMessage(
				'Na podany adres e-mail wysłaliśmy wiadomość z linkiem do aktywacji konta. Kliknij w link zawarty w wiadomości, aby dokończyć rejestrację.'
			);
			form.reset();
		},
		onError: (error: any) => {
			if (
				Array.isArray(error?.stack) &&
				error.stack.some(
					(item: any) =>
						typeof item === 'object' &&
						(item.email === 'userAlreadyExists' ||
							item.email === 'duplicatedEmail')
				)
			) {
				const msg = 'Ten adres email jest już zajęty';
				setStatus(SubmitStatus.ERROR);
				setResultMessage(msg);
				return;
			}

			setStatus(SubmitStatus.ERROR);
			const apiErrorMessage = getErrorCodes(error);
			setResultMessage(
				Array.isArray(apiErrorMessage)
					? apiErrorMessage.join(', ')
					: apiErrorMessage ||
							'Wystąpił błąd podczas rejestracji. Spróbuj ponownie.'
			);
		},
	});

	const handleSubmit = form.onSubmit((values: RegisterValues) => {
		if (values.password !== values.confirmPassword) {
			form.setFieldError('confirmPassword', 'Hasła nie są identyczne');
			return;
		}

		if (!values.acceptTerms || !values.acceptPrivacyPolicy) {
			if (!values.acceptTerms) {
				form.setFieldError(
					'acceptTerms',
					'Akceptacja regulaminu jest wymagana'
				);
			}
			if (!values.acceptPrivacyPolicy) {
				form.setFieldError(
					'acceptPrivacyPolicy',
					'Akceptacja polityki prywatności jest wymagana'
				);
			}
			return;
		}

		const registrationData = {
			companyName: values.companyName,
			contactPhoneNumber: values.contactPhoneNumber,
			contactEmail: values.contactEmail,
			email: values.contactEmail,
			password: values.password,
		};

		registerMutation.mutate(registrationData);
	});

	return (
		<div className="account-pages pt-5">
			<Container>
				<Row className="justify-content-center">
					<Col md={8} lg={6} xl={5}>
						<Card className="overflow-hidden mb-4">
							<LoadingOverlay visible={status === SubmitStatus.LOADING} />
							<div className="logo-bar text-center p-3">
								<img alt="logo" src={logo} height="40" />{' '}
								{/* Adjusted logo size */}
							</div>
							<CardBody className="p-4">
								{' '}
								{/* Added padding */}
								<div className="text-center">
									<CardTitle tag="h4" className="mb-4">
										Zarejestruj konto firmy
									</CardTitle>
								</div>
								<Form onSubmit={handleSubmit}>
									{/* Display General Error/Success Messages */}
									{status === SubmitStatus.ERROR && (
										<Alert color="danger" className="text-center mb-3">
											{resultMessage}
										</Alert>
									)}
									{status === SubmitStatus.SUCCESS && (
										<Alert color="success" className="text-center mb-3">
											{resultMessage}
										</Alert>
									)}

									{/* --- Dane Firmy --- */}
									<div className="mb-3">
										<Label className="form-label" htmlFor="companyName">
											Nazwa firmy *
										</Label>
										<Input
											id="companyName"
											placeholder="Wpisz nazwę firmy"
											{...form.getInputProps('companyName')}
											invalid={!!form.errors.companyName}
											required
										/>
										{form.errors.companyName && (
											<FormFeedback>{form.errors.companyName}</FormFeedback>
										)}
									</div>

									<div className="mb-3">
										<Label className="form-label" htmlFor="contactPhoneNumber">
											Numer telefonu (Kontakt z Biura Obsługi Klienta)
										</Label>
										<Input
											id="contactPhoneNumber"
											type="tel"
											placeholder="Wpisz numer telefonu (opcjonalnie)"
											{...form.getInputProps('contactPhoneNumber')}
											invalid={!!form.errors.contactPhoneNumber}
										/>
										{/* No feedback needed for optional field unless specific validation exists */}
									</div>

									{/* --- Dane Konta --- */}
									<div className="mb-3">
										<Label className="form-label" htmlFor="contactEmail">
											Adres email *
										</Label>
										<Input
											id="contactEmail"
											type="email"
											placeholder="Wpisz adres email"
											{...form.getInputProps('contactEmail')}
											invalid={!!form.errors.contactEmail}
											required
										/>
										{form.errors.contactEmail && (
											<FormFeedback>{form.errors.contactEmail}</FormFeedback>
										)}
									</div>

									<div className="mb-3">
										<Label className="form-label" htmlFor="password">
											Hasło *
										</Label>
										<Input
											id="password"
											type="password"
											placeholder="Wpisz hasło"
											{...form.getInputProps('password')}
											invalid={!!form.errors.password}
											required
										/>
										{form.errors.password && (
											<FormFeedback>{form.errors.password}</FormFeedback>
										)}
									</div>

									<div className="mb-3">
										<Label className="form-label" htmlFor="confirmPassword">
											Powtórz hasło *
										</Label>
										<Input
											id="confirmPassword"
											type="password"
											placeholder="Wpisz ponownie hasło"
											{...form.getInputProps('confirmPassword')}
											invalid={!!form.errors.confirmPassword}
											required
										/>
										{form.errors.confirmPassword && (
											<FormFeedback>{form.errors.confirmPassword}</FormFeedback>
										)}
									</div>

									{/* --- Akceptacja Regulaminów --- */}
									<div className="mb-3 form-check">
										<Input
											type="checkbox"
											className="form-check-input"
											id="acceptTerms"
											{...form.getInputProps('acceptTerms', {
												type: 'checkbox',
											})}
											invalid={!!form.errors.acceptTerms} // Show validation state
										/>
										<Label className="form-check-label" htmlFor="acceptTerms">
											Akceptuję{' '}
											<Link
												to="/terms"
												target="_blank"
												rel="noopener noreferrer"
											>
												Regulamin
											</Link>{' '}
											*
										</Label>
										{form.errors.acceptTerms && (
											<FormFeedback style={{ display: 'block' }}>
												{form.errors.acceptTerms}
											</FormFeedback>
										)}{' '}
										{/* Force display */}
									</div>

									<div className="mb-4 form-check">
										{' '}
										{/* Increased bottom margin */}
										<Input
											type="checkbox"
											className="form-check-input"
											id="acceptPrivacyPolicy"
											{...form.getInputProps('acceptPrivacyPolicy', {
												type: 'checkbox',
											})}
											invalid={!!form.errors.acceptPrivacyPolicy} // Show validation state
										/>
										<Label
											className="form-check-label"
											htmlFor="acceptPrivacyPolicy"
										>
											Akceptuję{' '}
											<Link
												to="/privacy-policy"
												target="_blank"
												rel="noopener noreferrer"
											>
												Politykę Prywatności
											</Link>{' '}
											*
										</Label>
										{form.errors.acceptPrivacyPolicy && (
											<FormFeedback style={{ display: 'block' }}>
												{form.errors.acceptPrivacyPolicy}
											</FormFeedback>
										)}{' '}
										{/* Force display */}
									</div>

									<div className="mt-3 d-grid">
										<Button
											color="primary"
											type="submit"
											disabled={status === SubmitStatus.LOADING}
										>
											{status === SubmitStatus.LOADING
												? 'Rejestrowanie...'
												: 'Zarejestruj'}
										</Button>
									</div>

									<div className="mt-4 text-center">
										<p className="text-muted mb-0">
											Masz już konto?{' '}
											<Link
												to={`/${AppPaths.LOGIN}`}
												className="fw-medium text-primary"
											>
												Zaloguj się
											</Link>
										</p>
									</div>
								</Form>
							</CardBody>
						</Card>
					</Col>
				</Row>
			</Container>
		</div>
	);
};
